import React, { useState, useMemo, useEffect } from 'react'
import st from './index.scss'
import cn from 'classnames'
import { useStore, observer } from '@/hooks/storeHooks'

/**
 * 分页器组件
 *
 * @param { int } pageNum 页码，默认是1
 * @param { int } pageSize 每页数量，默认是10
 * @param { int } total 总数，默认是每页数量（用每页数量当默认值，是希望即便是1页面的内容也显示分页器，分页器显示出来，哪怕只有1页，也是潜在的告诉用户所有内容已加载）
 * @param { func } onPageChange 页面改变事件 返回pagination对象，pagination对象包含三个字段：pageNum、pageSize、total
 */
export default observer(function Pagination({
    className,
    onPageChange,
    pageNum,
    pageSize,
    total,
    ...props
}) {
    const { isZhumavip } = useStore()
    const [pagination, setPagination] = useState(initPagination())

    // 是否使用外界值（如果有外界值传入，就是可控组件，不然就是不可控组件）
    const use_out_value = useMemo(
        () => !!(pageNum || pageSize),
        [pageNum, pageSize]
    )
    // 每次pagination更新，重新计算list
    const page_list = useMemo(() => {
        let pagingNum = Math.ceil(pagination.total / pagination.pageSize)
        let pagingList =
            (pagingNum &&
                new Array(pagingNum)
                    ?.fill('')
                    ?.map((item, index) => index + 1)) ||
            []
        // 处理分页器点点点
        return makePage(pagingList.length, pagination.pageNum, 2)
    }, [pagination])

    // 每次新值传入，更新pagination
    useEffect(() => {
        setPagination(initPagination())
    }, [pageNum, pageSize, total])

    function initPagination() {
        const pg_size = parseInt(pageSize || 10)
        return {
            pageNum: parseInt(pageNum || 1),
            pageSize: pg_size,
            total: total || pg_size
        }
    }

    // 页面变动事件
    function handlePageChange(new_pagination) {
        // 不可控组件 在内部自己管理表现
        !use_out_value && setPagination(new_pagination)
        onPageChange && onPageChange(new_pagination)
    }

    // 上一页
    function PreviousPage() {
        // 没有上一页
        if (pagination.pageNum - 1 <= 0) return

        const new_pagination = {
            ...pagination
        }
        new_pagination.pageNum--

        handlePageChange(new_pagination)
    }

    // 下一页
    function nextPage() {
        // 超过最大页数
        if (
            parseInt(pagination.pageNum) + 1 >
            Math.ceil(pagination.total / pagination.pageSize)
        )
            return

        const new_pagination = {
            ...pagination
        }
        new_pagination.pageNum++

        handlePageChange(new_pagination)
    }

    // 点击分页
    function clickPaging(item) {
        const new_pagination = {
            ...pagination
        }

        new_pagination.pageNum = item
        handlePageChange(new_pagination)
    }

    /**
     * 分页器展示点点点 https://juejin.cn/post/6844903682933260295
     * @param {Number} total 分页总数量
     * @param {Number} cur 当前所在分页
     * @param {Numbre} around (上一页 + 下一页)个数
     */
    function makePage(total, cur, around) {
        let result = []
        let baseCount = around * 2 + 1 + 2 + 2 + 2 //总共元素个数
        let surplus = baseCount - 4 //只出现一个省略号 剩余元素个数
        let startPosition = 1 + 2 + around + 1 //前面出现省略号的临界点
        let endPosition = total - 2 - around - 1 //后面出现省略号的临界点

        if (total <= baseCount - 2) {
            //全部显示 不出现省略号
            result = Array.from({ length: total }, (v, i) => i + 1)
        } else {
            //需要出现省略号
            if (cur < startPosition) {
                //1.只有后面出现省略号
                result = [
                    ...Array.from({ length: surplus }, (v, i) => i + 1),
                    '...',
                    total
                ]
            } else if (cur > endPosition) {
                //2.只有前边出现省略号
                result = [
                    1,
                    '...',
                    ...Array.from(
                        { length: surplus },
                        (v, i) => total - surplus + i + 1
                    )
                ]
            } else {
                //3.两边都有省略号
                result = [
                    1,
                    '...',
                    ...Array.from(
                        { length: around * 2 + 1 },
                        (v, i) => cur - around + i
                    ),
                    '...',
                    total
                ]
            }
        }

        return result
    }

    if (!pagination.total) return null

    return (
        <div
            className={cn(
                {
                    [st['pagingBox']]: true,
                    [st['zhongheStyle']]: !isZhumavip(),
                    [st['zhumaStyle']]: isZhumavip()
                },
                className
            )}
            {...props}
        >
            <div className={st['paging']}>
                <div
                    className={st['PreviousPage']}
                    onClick={() => PreviousPage()}
                ></div>
                <div className={st['pagingCon']}>
                    {page_list.map((ele, index) => {
                        return (
                            <span
                                key={index}
                                className={cn({
                                    [st['activePaging']]:
                                        ele == pagination.pageNum
                                })}
                                onClick={() => {
                                    if (ele == '...') return
                                    clickPaging(ele)
                                }}
                            >
                                {ele}
                            </span>
                        )
                    })}
                </div>
                <div
                    className={st['nextPage']}
                    onClick={() => nextPage()}
                ></div>
            </div>
        </div>
    )
})
